import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  Flame,
  Trophy,
  Hand,
  BarChart3,
  Lightbulb,
  Target,
} from "lucide-react";
import { useCompanion } from "../../context/CompanionProvider";
import { Button } from "../ui/button";
import { useIsMobile } from "../../hooks/use-mobile";
import type { InsightType } from "../../api/proactiveInsights";

// ─── Insight Type Config ─────────────────────────────────────────────────────

const INSIGHT_CONFIG: Record<
  InsightType,
  { emoji: string; icon: React.ElementType }
> = {
  streak_milestone: { emoji: "\uD83D\uDD25", icon: Flame },
  completion_milestone: { emoji: "\uD83C\uDFC6", icon: Trophy },
  idle_nudge: { emoji: "\uD83D\uDC4B", icon: Hand },
  progress_summary: { emoji: "\uD83D\uDCCA", icon: BarChart3 },
  pattern_detected: { emoji: "\uD83D\uDCA1", icon: Lightbulb },
  outcome_tracking: { emoji: "\uD83C\uDFAF", icon: Target },
  weekly_summary: { emoji: "\uD83D\uDCC5", icon: BarChart3 },
  monthly_summary: { emoji: "\uD83D\uDCC6", icon: BarChart3 },
};

// ─── Component ───────────────────────────────────────────────────────────────

const FloatingCompanion: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { currentFloatingInsight, dismissInsight } = useCompanion();

  // Hide during activity runs and on portal (InsightIndicator already covers portal)
  if (
    location.pathname.startsWith("/activities/run") ||
    location.pathname === "/portal"
  ) {
    return null;
  }

  const insight = currentFloatingInsight;
  if (!insight) return null;

  const typeConfig = INSIGHT_CONFIG[insight.insightType] || {
    emoji: "\u2728",
    icon: Lightbulb,
  };

  return (
    <div
      key={insight._id}
      className="fixed end-4 z-40 max-w-sm w-80 animate-companion-enter"
      style={isMobile ? { bottom: "calc(64px + env(safe-area-inset-bottom, 0px))" } : { bottom: "1rem" }}
    >
      <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl shadow-ai/10 border border-ai/10 p-4 relative">
        {/* Dismiss */}
        <button
          onClick={() => dismissInsight(insight._id)}
          className="absolute top-3 end-3 text-gray-300 hover:text-gray-500 transition-colors"
          aria-label={t("dismiss", "Dismiss")}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-ai/10 to-ai/5">
            <span className="text-lg select-none">{typeConfig.emoji}</span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pe-4">
            <h4 className="text-sm font-semibold text-gray-900 leading-tight">
              {insight.title}
            </h4>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              {insight.message}
            </p>

            {/* Suggested actions */}
            {insight.suggestedActions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {insight.suggestedActions.map((action, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-ai/20 text-ai hover:bg-ai/5"
                    onClick={() => {
                      if (action.actionUrl) {
                        navigate(action.actionUrl);
                      }
                      dismissInsight(insight._id);
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingCompanion;
